import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  MessageCircle, 
  Send, 
  Clock,
  CheckCircle,
  Mail
} from "lucide-react";

const QAPage = () => {
  const { token } = useAuth();
  const [myQuestions, setMyQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    message: ""
  });

  useEffect(() => {
    fetchMyQuestions();
  }, []);

  const fetchMyQuestions = async () => {
    try {
      const response = await axios.get(`${API}/qa/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyQuestions(response.data);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/qa/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Question submitted! We'll get back to you soon.");
      setFormData({ subject: "", message: "" });
      fetchMyQuestions();
    } catch (error) {
      toast.error("Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Q&A Support" subtitle="Got questions? We've got answers!">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Submit Question Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-white card-shadow border-0">
            <CardHeader>
              <CardTitle className="text-[var(--deep-ocean)] flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Ask a Question
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="e.g., Best bait for yellowfish?"
                    value={formData.subject}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="qa-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Your Question</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Describe your question in detail..."
                    value={formData.message}
                    onChange={handleChange}
                    className="input-field min-h-[120px]"
                    data-testid="qa-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                  data-testid="qa-submit-btn"
                >
                  {submitting ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Question
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-[var(--bg-main)] rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[var(--deep-ocean)] mt-0.5" />
                  <div>
                    <p className="text-sm text-[var(--river-stone)]">
                      Your question will be sent to our team. You'll receive a response at your registered email address.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* My Questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-xl font-bold text-[var(--deep-ocean)] mb-4">My Questions</h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : myQuestions.length > 0 ? (
            <div className="space-y-4">
              {myQuestions.map((question, index) => (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-white card-shadow border-0">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-[var(--deep-ocean)]">{question.subject}</h3>
                        <Badge className={question.status === "answered" ? "bg-[var(--success)]" : "bg-yellow-500"}>
                          {question.status === "answered" ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Answered</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-[var(--river-stone)] mb-3">{question.message}</p>
                      
                      <p className="text-xs text-[var(--river-stone)]">
                        Asked on {new Date(question.created_at).toLocaleDateString()}
                      </p>

                      {question.admin_response && (
                        <div className="mt-4 p-4 bg-[var(--deep-ocean)]/5 rounded-lg border-l-4 border-[var(--deep-ocean)]">
                          <p className="text-sm font-semibold text-[var(--deep-ocean)] mb-2">Response from FishingFanatix:</p>
                          <p className="text-sm text-[var(--river-stone)]">{question.admin_response}</p>
                          {question.responded_at && (
                            <p className="text-xs text-[var(--river-stone)] mt-2">
                              Responded on {new Date(question.responded_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="bg-white card-shadow border-0">
              <CardContent className="p-8 text-center">
                <MessageCircle className="w-12 h-12 text-[var(--river-stone)] mx-auto mb-4" />
                <p className="text-[var(--river-stone)]">
                  You haven't asked any questions yet. Use the form above to get started!
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default QAPage;
